import React, { useState, useRef, useEffect } from "react";
import {
  Input,
  Button,
  Select,
  message,
  Checkbox,
  Form,
  Space,
  Upload,
  Modal,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { PlusOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import eyeIcon from "../../../Assets/Icons/eye.svg";
import addIcon from "../../../Assets/Icons/uploadbtn.svg";
import { EyeOutlined } from "@ant-design/icons";
import editIcon from "../../../Assets/Icons/edit.svg";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "./style.css";
import { useParams } from "react-router-dom";
import { getCompanybyId } from "../../../API/Admin/company";
import { editCompany } from "../../../API/Admin/company";
import { getCustomerbyCompanyId } from "../../../API/User/customer";

const ViewRecoveryCompanyDetail = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const status = Form.useWatch("status", form);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { id } = useParams();
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [logoFile, setLogoFile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [isDisabled, setIsDisabled] = useState(true);

  const handleEdit = () => {
    setIsDisabled((prev) => !prev);
  };

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const customerColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("customer_name"),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("email"),
    },
    {
      title: "Phone Number",
      dataIndex: "customer_phone",
      key: "customer_phone",
      ...getColumnSearchProps("customer_phone"),
    },
    {
      title: "No. of Vehicles",
      dataIndex: "vehicles",
      key: "vehicles",
      sorter: (a, b) => a.vehicles - b.vehicles,
      render: (vehicles) => (
        <span className="vehicle-badge">
          {vehicles} Vehicle{vehicles !== 1 ? "s" : ""}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",

      render: (status) => {
        const statusText = status ? "Active" : "Inactive";
        const statusClass = status ? "active" : "inactive";

        return (
          <span className={`status-badge ${statusClass}`}>{statusText}</span>
        );
      },

      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],

      onFilter: (value, record) => record.status === value,
    },
  ];

  const fetchDataById = async (id) => {
    try {
      setLoading(true);
      const response = await getCompanybyId(id);

      if (Math.floor(response?.status / 100) === 2) {
        const company = response?.data?.company;

        if (company) {
          const fullLogoUrl = company.logo
            ? `${process.env.REACT_APP_MEDIA_URL}${company.logo}`
            : null;

          setLogoUrl(fullLogoUrl);

          form.setFieldsValue({
            companyName: company.company_name,
            companyAddress: company.company_address,
            registrationNumber: company.registration_number || "",
            coverageArea: company.coverage_area || "",
            primaryContactPerson: company.primary_contact_person || "",
            primaryContactNumber: company.primary_contact_number || "",
            is_default: company.is_default,
            secondaryContactNumber: company.secondary_contact_number || "",
            emailAddress: company.email_address || "",
            serviceLevelAgreement:
              company.service_level_agreement_details || "",
            isDefault: company.is_default,
            status: company.status,
          });
        }
      } else {
        messageApi.error("Unexpected response from server.");
      }
    } catch (err) {
      messageApi.error("Failed to fetch company.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomersByCompany = async (companyId) => {
    try {
      const res = await getCustomerbyCompanyId(companyId);

      if (Math.floor(res.status / 100) === 2) {
        const arr = res?.data?.customer || [];

        const formatted = arr.map((cust) => ({
          key: cust.id,
          customer_name: cust.customer_name,
          email: cust.email,
          customer_phone: cust.customer_phone,
          vehicles: cust.vehicles?.length || 0,

          status: cust.status,
        }));

        setCustomers(formatted);
      }
    } catch (err) {
      console.log("Error fetching customers:", err);
    }
  };
  useEffect(() => {
    if (id) {
      fetchDataById(id);
    }
    fetchCustomersByCompany(id);
  }, [id]);

  const handleSubmit = async (values, override_default) => {
    setLoading(true);

    try {
      const res = await editCompany(
        id,
        values?.companyName,
        values?.companyAddress,
        values?.registrationNumber,
        values?.coverageArea,
        values?.primaryContactPerson,
        values?.primaryContactNumber,
        values?.secondaryContactNumber,
        values?.emailAddress,
        values?.serviceLevelAgreement,
        values?.is_default,
        values?.status,
        logoFile,
        override_default,
      );

      const isDefaultError = res?.data?.is_default;
      if (
        res.status === 400 &&
        Array.isArray(isDefaultError) &&
        isDefaultError[0]?.includes("Default company already exists")
      ) {
        setPendingValues(values);
        setIsModalOpen(true);
        setLoading(false);
        return;
      }

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success("Company updated successfully!");
        setIsDisabled(true);
        setIsModalOpen(false);
        fetchDataById(id);
      } else {
        errorNotification(res?.data?.message || "Failed to update company.");
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (
        errorData?.is_default?.[0]?.includes("Default company already exists")
      ) {
        setPendingValues(form.getFieldsValue());
        setIsModalOpen(true);
      } else {
        errorNotification("Error updating company.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSaved(false);
    message.info("Changes canceled");
  };

  return (
    <div className="recovery-company-detail">
      {contextHolder}
      <div className="form-details">
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="company-details">
            <div className="header">
              <div className="heading">Recovery Company</div>
              <div className="show-status">
                {isDisabled ? (
                  <span
                    className={`status-badge ${(
                      status ||
                      form.getFieldValue("status") ||
                      ""
                    ).toLowerCase()}`}
                  >
                    {status || form.getFieldValue("status")}
                  </span>
                ) : (
                  <Form.Item
                    name="status"
                    style={{ margin: 0 }}
                    rules={[
                      { required: true, message: "Please select a status" },
                    ]}
                  >
                    <Select placeholder="Select Status" style={{ width: 150 }}>
                      <Select.Option value="Active">Active</Select.Option>
                      <Select.Option value="Inactive">Inactive</Select.Option>
                    </Select>
                  </Form.Item>
                )}
              </div>
            </div>
            <div className="sub-header">
              <div className="logo-wrapper">
                <img src={logoUrl || "Null"} alt="Logo" className="logo-icon" />

                {!isDisabled && (
                  <div
                    className="edit-overlay"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <img src={editIcon} alt="edit logo" className="edit-icon" />
                  </div>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const allowedExtensions = ["png", "jpg", "jpeg", "jfif"];
                    const fileExtension = file.name
                      .split(".")
                      .pop()
                      .toLowerCase();

                    if (allowedExtensions.includes(fileExtension)) {
                      setLogoUrl(URL.createObjectURL(file));
                      setLogoFile(file);
                    } else {
                      message.error(
                        "Only image files (.png, .jpg, .jpeg, .jfif) are allowed",
                      );
                    }
                  }}
                />
              </div>

              <div className="info ">
                <div className="sub-info">
                  {form.getFieldValue("companyName")}
                </div>
                <div className="sub-mail">
                  {form.getFieldValue("emailAddress")}
                </div>
                <div className="sub-contact">
                  {form.getFieldValue("primaryContactNumber")}
                </div>
                <Button
                  type="default"
                  onClick={handleEdit}
                  className="edit-btn"
                >
                  <img
                    src={isDisabled ? editIcon : undefined}
                    alt={isDisabled ? "edit user" : "view user"}
                    className="btn-icon"
                    style={{ display: isDisabled ? "inline" : "none" }}
                  />
                  {!isDisabled && <EyeOutlined />}
                  {isDisabled ? "Edit" : "View"}
                </Button>
              </div>
            </div>

            <div className="details">
              <div className="input-group">
                <div className="title">Registration Number</div>
                <Form.Item
                  name="registrationNumber"
                  rules={[
                    {
                      required: true,
                      message: "Please enter registration number!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter registration number"
                    disabled={isDisabled}
                  />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Office Address</div>
                <Form.Item
                  name="companyAddress"
                  rules={[
                    { required: true, message: "Please enter office address!" },
                  ]}
                >
                  <Input placeholder="Enter address" disabled={isDisabled} />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Coverage Area</div>
                <Form.Item
                  name="coverageArea"
                  rules={[
                    { required: true, message: "Please enter coverage area!" },
                  ]}
                >
                  <Input placeholder="Add Location" disabled={isDisabled} />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Company Name</div>
                <Form.Item
                  name="companyName"
                  rules={[
                    { required: true, message: "Please enter company name!" },
                  ]}
                >
                  <Input
                    placeholder="Enter Company Name"
                    disabled={isDisabled}
                  />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Company Email</div>
                <Form.Item
                  name="emailAddress"
                  rules={[
                    { required: true, message: "Please enter company email!" },
                    { type: "email", message: "Please enter a valid email!" },
                  ]}
                >
                  <Input
                    placeholder="Enter Company Email"
                    disabled={isDisabled}
                  />
                </Form.Item>
              </div>
              <div className="input-group">
                <div className="title">Company Contact</div>
                <Form.Item
                  name="primaryContactNumber"
                  rules={[
                    {
                      required: true,
                      message: "Please enter company contact!",
                    },
                  ]}
                >
                  <PhoneInput
                    country={"jm"}
                    placeholder="Enter number"
                    inputClass={`phone-input ${isDisabled ? "disabled" : ""}`}
                    specialLabel={false}
                    inputStyle={{ width: "100%" }}
                    disabled={isDisabled}
                    onChange={(value) => {
                      form.setFieldsValue({
                        primaryContactNumber: value.startsWith("+")
                          ? value
                          : `+${value}`,
                      });
                    }}
                  />
                </Form.Item>
              </div>
              <div className="input-group">
                <div className="title">Default</div>
                <Form.Item
                  name="is_default"
                  rules={[
                    {
                      required: true,
                      message: "Please select default option",
                    },
                  ]}
                >
                  <Select placeholder="Select Option" disabled={isDisabled}>
                    <Select.Option value={true}>True</Select.Option>
                    <Select.Option value={false}>False</Select.Option>
                  </Select>
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button disabled={isDisabled} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              disabled={isDisabled}
              className="save-btn"
              type="primary"
              htmlType="submit"
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </div>
      {/* <div className="map-details">Map here</div> */}

      <div className="table">
        <GenericTable dataSource={customers} columns={customerColumns} />
      </div>
      <Modal
        title="Override Default Company?"
        open={isModalOpen}
        onOk={() => handleSubmit(pendingValues, true)}
        onCancel={() => setIsModalOpen(false)}
        okText="Yes, Override"
        cancelText="No"
        confirmLoading={loading}
      >
        <p>
          A default company already exists. Are you sure you want to make this
          company the default?
        </p>
      </Modal>
    </div>
  );
};

export default ViewRecoveryCompanyDetail;
